/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import moment from 'moment';
import { fetchSignalIndex } from '../../../entities/helpers/thankview-helper';
import { getBrowserTimezone } from '../utils/video-helpers';

const PAGE_LIMIT = 50;

const useFetchThankviewVideos = (
  getValidToken,
  thankViewUserId,
  startDate,
  endDate,
  videoFilters,
  observerTarget
) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNum, setPageNum] = useState(1);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const token = await getValidToken();
      if (token) {
        const filters = {
          is_signal: true,
          name: videoFilters.searchText,
          user_id: thankViewUserId,
          from_date: startDate ? moment(startDate).tz(getBrowserTimezone()).startOf('day').format() : '',
          to_date: endDate ? moment(endDate).tz(getBrowserTimezone()).endOf('day').format() : '',
        };

        const payload = {
          sort: videoFilters.sortSelected.value,
          order: videoFilters.isAscOrder ? 'asc' : 'desc',
          page: pageNum,
          limit: PAGE_LIMIT,
          filters: JSON.stringify(filters),
        };

        const { data } = await fetchSignalIndex(payload, token);
        setTotalCount(data.count);

        const libraryVideoMap = data.libraryVideos.reduce((map, video) => {
          map[video.id] = video;
          return map;
        }, {});

        const fetchedVideos = data.personalVideos.map((video) => ({
          ...video,
          libraryVideo: libraryVideoMap[video.library_video_id] || null,
        }));
        setVideos((prev) => (pageNum > 1 ? prev.concat(fetchedVideos) : fetchedVideos));
        return videos;
      }
    } catch (err) {
      console.error('Error fetching data in video library:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetVideos = (fetchVideos) => {
    setVideos([]);
    if (pageNum > 1) {
      setPageNum(1);
    } else if (fetchVideos) {
      fetchData();
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && totalCount > 0 && videos.length < totalCount) {
          setPageNum((prev) => prev + 1);
        }
      },
      { threshold: 1.0 }
    );
    const target = observerTarget.current;
    if (target) observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, [loading, totalCount]);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, videoFilters, pageNum]);

  return { videos, loading, totalCount, resetVideos };
};

export default useFetchThankviewVideos;
